import React, { useEffect, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  useGetSiteSettingsQuery,
  useUpdateSiteSettingsMutation,
  type SiteSettings,
  siteSettingsDefaults,
} from "../../store/api/siteSettingsApi";
import "./admin.css";

const Field: React.FC<{
  label: string;
  icon: string;
  accent: string;
  bg: string;
  border: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, icon, accent, bg, border, hint, children }) => (
  <div style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 12, marginBottom: 18 }}>
    <label className="form-label mb-1" style={{ fontWeight: 700, fontSize: "0.82rem", color: "#334155", display: "flex", alignItems: "center", gap: 5 }}>
      <i className={icon} style={{ color: accent, fontSize: "0.78rem" }} />
      {label}
    </label>
    {hint && <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginBottom: 4 }}>{hint}</div>}
    <div style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 9, overflow: "hidden" }}>
      {children}
    </div>
  </div>
);

const ContactSettingsPage = () => {
  const { data: loaded, isLoading } = useGetSiteSettingsQuery();
  const [save, { isLoading: isSaving }] = useUpdateSiteSettingsMutation();

  const [form, setForm] = useState<SiteSettings>(siteSettingsDefaults);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    if (loaded) setForm(loaded);
  }, [loaded]);

  const set = (field: keyof SiteSettings, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setResult(null);
    try {
      await save(form).unwrap();
      setResult({ ok: true, msg: "Contact details saved successfully." });
    } catch (err) {
      setResult({ ok: false, msg: getApiErrorMessage(err as FetchBaseQueryError) });
    }
  };

  const inputStyle = {
    border: "none",
    background: "transparent",
    padding: "9px 12px",
    width: "100%",
    fontSize: "0.88rem",
    color: "#0f172a",
    outline: "none",
  } as React.CSSProperties;

  const textareaStyle = { ...inputStyle, resize: "vertical" as const, minHeight: 60 };

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#134e4a,#0d9488,#14b8a6)", padding: "12px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-gear-fill" style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#fff", lineHeight: 1.2 }}>Contact & Site Settings</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.72)" }}>Update phone, email, address, hours, and social links shown on the website</div>
              </div>
            </div>
            {loaded && (
              <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.65)" }}>
                <i className="bi bi-check2-circle me-1" />Last saved: {new Date(loaded as any).toLocaleDateString?.() ?? "—"}
              </span>
            )}
          </div>

          <div style={{ padding: "24px 28px" }}>
            {isLoading ? (
              <div className="alert alert-light border">Loading settings…</div>
            ) : (
              <div className="row g-4">

                {/* ── Contact Info ────────────────────────────────────────── */}
                <div className="col-lg-6">
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748b", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="bi bi-telephone-fill" style={{ color: "#0d9488" }} />Contact Information
                    </div>

                    <Field label="Phone Number" icon="bi bi-telephone-fill" accent="#0d9488" bg="#f0fdfa" border="#99f6e4"
                      hint="Shown in header, footer, and contact page — include country code">
                      <input style={inputStyle} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 81828 33009" title="Phone number" />
                    </Field>

                    <Field label="Email Address" icon="bi bi-envelope-fill" accent="#6366f1" bg="#eef2ff" border="#c7d2fe"
                      hint="Primary contact email for enquiries">
                      <input style={inputStyle} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="info@dialin24.com" title="Email address" />
                    </Field>

                    <Field label="Office Address" icon="bi bi-geo-alt-fill" accent="#f59e0b" bg="#fefce8" border="#fde68a"
                      hint="Full address shown on contact page">
                      <textarea style={textareaStyle} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Lucknow, Uttar Pradesh" title="Office address" rows={2} />
                    </Field>

                    <Field label="WhatsApp Number" icon="bi bi-whatsapp" accent="#16a34a" bg="#f0fdf4" border="#86efac"
                      hint="Optional — with country code (+91...)">
                      <input style={inputStyle} value={form.whatsApp ?? ""} onChange={(e) => set("whatsApp", e.target.value)} placeholder="+91 81828 33009" title="WhatsApp number" />
                    </Field>
                  </div>

                  {/* ── Social Links ──────────────────────────────────── */}
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px" }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748b", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="bi bi-share-fill" style={{ color: "#6366f1" }} />Social Links <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 4 }}>(optional)</span>
                    </div>

                    <Field label="Facebook Page URL" icon="bi bi-facebook" accent="#1d4ed8" bg="#eff6ff" border="#bfdbfe">
                      <input style={inputStyle} value={form.facebookUrl ?? ""} onChange={(e) => set("facebookUrl", e.target.value)} placeholder="https://facebook.com/dialin24" title="Facebook URL" />
                    </Field>

                    <Field label="Instagram Profile URL" icon="bi bi-instagram" accent="#db2777" bg="#fdf2f8" border="#fbcfe8">
                      <input style={inputStyle} value={form.instagramUrl ?? ""} onChange={(e) => set("instagramUrl", e.target.value)} placeholder="https://instagram.com/dialin24" title="Instagram URL" />
                    </Field>

                    <Field label="YouTube Channel URL" icon="bi bi-youtube" accent="#dc2626" bg="#fef2f2" border="#fecaca">
                      <input style={inputStyle} value={form.youtubeUrl ?? ""} onChange={(e) => set("youtubeUrl", e.target.value)} placeholder="https://youtube.com/@dialin24" title="YouTube URL" />
                    </Field>
                  </div>
                </div>

                {/* ── Business Hours + Map ─────────────────────────────── */}
                <div className="col-lg-6">
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748b", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="bi bi-clock-fill" style={{ color: "#f59e0b" }} />Business Hours
                    </div>

                    <Field label="Monday – Friday" icon="bi bi-calendar-week-fill" accent="#0ea5e9" bg="#e0f2fe" border="#bae6fd">
                      <input style={inputStyle} value={form.hoursWeekday} onChange={(e) => set("hoursWeekday", e.target.value)} placeholder="9:00 AM - 7:00 PM" title="Weekday hours" />
                    </Field>

                    <Field label="Saturday" icon="bi bi-calendar3" accent="#8b5cf6" bg="#ede9fe" border="#ddd6fe">
                      <input style={inputStyle} value={form.hoursSaturday} onChange={(e) => set("hoursSaturday", e.target.value)} placeholder="9:00 AM - 5:00 PM" title="Saturday hours" />
                    </Field>

                    <Field label="Project Support Note" icon="bi bi-headset" accent="#0d9488" bg="#f0fdfa" border="#99f6e4"
                      hint="Short note displayed under hours, e.g. 'quick response available'">
                      <input style={inputStyle} value={form.hoursSupport} onChange={(e) => set("hoursSupport", e.target.value)} placeholder="quick response available" title="Support note" />
                    </Field>
                  </div>

                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748b", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="bi bi-pin-map-fill" style={{ color: "#0ea5e9" }} />Google Maps Embed
                    </div>

                    <Field label="Map Embed URL" icon="bi bi-map-fill" accent="#0ea5e9" bg="#e0f2fe" border="#bae6fd"
                      hint='Paste the src="" URL from Google Maps → Share → Embed a map'>
                      <textarea style={{ ...textareaStyle, fontSize: "0.78rem" }} value={form.mapEmbedUrl ?? ""} onChange={(e) => set("mapEmbedUrl", e.target.value)} placeholder="https://www.google.com/maps?q=..." title="Map embed URL" rows={3} />
                    </Field>

                    {form.mapEmbedUrl && (
                      <div style={{ borderRadius: 10, overflow: "hidden", border: "1.5px solid #e2e8f0", marginTop: 8 }}>
                        <div style={{ fontSize: "0.68rem", color: "#64748b", padding: "4px 10px", background: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>Preview</div>
                        <div className="ratio ratio-4x3">
                          <iframe src={form.mapEmbedUrl} title="Map preview" allowFullScreen loading="lazy" style={{ border: 0 }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Save */}
                  {result && (
                    <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: result.ok ? "#f0fdf4" : "#fef2f2", border: `1px solid ${result.ok ? "#86efac" : "#fca5a5"}`, display: "flex", alignItems: "center", gap: 8 }}>
                      <i className={result.ok ? "bi bi-check-circle-fill" : "bi bi-x-circle-fill"} style={{ color: result.ok ? "#16a34a" : "#dc2626", fontSize: "1rem" }} />
                      <span style={{ fontSize: "0.84rem", fontWeight: 600, color: result.ok ? "#15803d" : "#dc2626" }}>{result.msg}</span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{ background: isSaving ? "#e2e8f0" : "linear-gradient(135deg,#0d9488,#0f766e)", color: isSaving ? "#94a3b8" : "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 700, fontSize: "0.9rem", cursor: isSaving ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 8, boxShadow: isSaving ? "none" : "0 4px 14px #0d948844" }}
                  >
                    {isSaving
                      ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Saving…</>
                      : <><i className="bi bi-check2-square" /> Save Settings</>}
                  </button>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSettingsPage;
